import { normalizeChamferIgnoredOptions } from '../../../utils/feature/chamferPropertyDescriptor';
import { FeatureDefinition, type FeatureDefinitionSnapshot } from '../FeatureDefinition';

/**
 * 倒角特征定义快照。
 */
export interface ChamferFeatureDefinitionSnapshot extends FeatureDefinitionSnapshot {
    /** 当前特征种类。 */
    featureKind: 'chamfer';
    /** 原始命令参数。 */
    commandParams: ChamferParams;
}

/**
 * 倒角特征定义投影。
 */
export interface ChamferFeatureDefinitionProjection {
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 结果对象 ID。 */
    resultObjectId: string | null;
    /** 倒角定义。 */
    definition: ChamferFeatureDefinition;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 倒角特征定义对象。
 */
export class ChamferFeatureDefinitionModel extends FeatureDefinition<ChamferFeatureDefinitionSnapshot> {
    /**
     * 基于命令参数创建倒角定义对象。
     *
     * @param input 输入参数。
     * @returns 倒角定义对象。
     */
    public static fromParams(input: {
        commandParams: ChamferParams;
        version?: number;
    }): ChamferFeatureDefinitionModel {
        const commandParams = structuredClone(input.commandParams);
        return new ChamferFeatureDefinitionModel({
            featureKind: 'chamfer',
            version: input.version ?? 1,
            commandParams: {
                ...commandParams,
                definition: normalizeChamferIgnoredOptions(commandParams.definition),
            },
        });
    }

    /**
     * @param snapshot 定义快照。
     */
    public constructor(snapshot: ChamferFeatureDefinitionSnapshot) {
        super(snapshot);
    }

    /**
     * 获取原始命令参数。
     *
     * @returns 命令参数快照。
     */
    public getCommandParams(): ChamferParams {
        return this.toSnapshot().commandParams;
    }

    /**
     * 获取倒角领域定义。
     *
     * @returns 倒角定义快照。
     */
    public getBlendDefinition(): ChamferFeatureDefinition {
        return structuredClone(this.toSnapshot().commandParams.definition);
    }

    /**
     * 获取上游实体对象 ID。
     *
     * @returns 上游实体对象 ID。
     */
    public getUpstreamBodyObjectId(): string | null {
        return typeof this.toSnapshot().commandParams.upstreamBodyObjectId === 'string'
            ? this.toSnapshot().commandParams.upstreamBodyObjectId
            : null;
    }

    /**
     * 生成面向 UI 的投影快照。
     *
     * @returns 投影快照。
     */
    public toProjection(): ChamferFeatureDefinitionProjection {
        const commandParams = this.toSnapshot().commandParams;
        return {
            upstreamBodyObjectId: typeof commandParams.upstreamBodyObjectId === 'string'
                ? commandParams.upstreamBodyObjectId
                : null,
            resultObjectId: typeof commandParams.resultObjectId === 'string'
                ? commandParams.resultObjectId
                : null,
            definition: structuredClone(commandParams.definition),
            name: typeof commandParams.name === 'string' ? commandParams.name : null,
        };
    }

    /**
     * 基于新命令参数生成下一份定义对象。
     *
     * @param commandParams 新命令参数。
     * @returns 新定义对象。
     */
    public withCommandParams(commandParams: ChamferParams): ChamferFeatureDefinitionModel {
        return ChamferFeatureDefinitionModel.fromParams({
            commandParams,
            version: this.getVersion(),
        });
    }
}
